/**
 * Pure geometry for smart guides. No DOM access — every function here takes and
 * returns plain rects in pixels, measured relative to the slide root.
 */

export const SNAP_TOLERANCE_PX = 6;
export const GUIDE_OVERHANG_PX = 8;
export const SIZE_MARK_OFFSET_PX = 6;
export const MIN_SIZE_PX = 20;

const EPSILON = 0.01;

const X_EDGES = ['left', 'centre', 'right'];
const Y_EDGES = ['top', 'centre', 'bottom'];

/** Which edges a resize handle actually drags. A move drags all of them. */
const MOVING_EDGES = {
  left: { x: ['left'], y: [] },
  right: { x: ['right'], y: [] },
  top: { x: [], y: ['top'] },
  bottom: { x: [], y: ['bottom'] },
  'top-left': { x: ['left'], y: ['top'] },
  'top-right': { x: ['right'], y: ['top'] },
  'bottom-left': { x: ['left'], y: ['bottom'] },
  'bottom-right': { x: ['right'], y: ['bottom'] },
};

const edgesFor = (handle, axis) => {
  if (!handle) return axis === 'x' ? X_EDGES : Y_EDGES;
  return MOVING_EDGES[handle] ? MOVING_EDGES[handle][axis] : [];
};

const xAt = (rect, edge) =>
  edge === 'left'
    ? rect.left
    : edge === 'right'
      ? rect.left + rect.width
      : rect.left + rect.width / 2;

const yAt = (rect, edge) =>
  edge === 'top'
    ? rect.top
    : edge === 'bottom'
      ? rect.top + rect.height
      : rect.top + rect.height / 2;

const slideRect = (slide) => ({ left: 0, top: 0, width: slide.width, height: slide.height });

/** Every position the active object could align to on one axis. */
const candidatesOn = (axis, targets, slide) => {
  const at = axis === 'x' ? xAt : yAt;
  const edges = axis === 'x' ? X_EDGES : Y_EDGES;
  const candidates = [];

  targets.forEach((target) => {
    edges.forEach((edge) => {
      candidates.push({ position: at(target, edge), source: 'object', rect: target });
    });
  });

  const slideBounds = slideRect(slide);
  edges.forEach((edge) => {
    candidates.push({ position: at(slideBounds, edge), source: 'slide', rect: slideBounds });
  });

  return candidates;
};

/** Nearest wins; a tie goes to the slide guide. */
const beats = (distance, source, best) =>
  distance < best.distance ||
  (distance === best.distance && source === 'slide' && best.source !== 'slide');

const chooseAlignment = (active, candidates, axis, edges) => {
  const at = axis === 'x' ? xAt : yAt;
  let best = null;

  edges.forEach((activeEdge) => {
    const from = at(active, activeEdge);
    candidates.forEach((candidate) => {
      const delta = candidate.position - from;
      const distance = Math.abs(delta);
      if (distance > SNAP_TOLERANCE_PX) return;
      if (best && !beats(distance, candidate.source, best)) return;
      best = { kind: 'align', delta, distance, source: candidate.source };
    });
  });

  return best;
};

/** Nearest other object whose size this one could match without going too small. */
const chooseSize = (current, sizes) => {
  let best = null;

  sizes.forEach((size) => {
    const delta = size - current;
    const distance = Math.abs(delta);
    if (distance > SNAP_TOLERANCE_PX || current + delta < MIN_SIZE_PX) return;
    if (best && distance >= best.distance) return;
    best = { kind: 'size', delta, distance };
  });

  return best;
};

/** Alignment wins a tie — it positions an edge, which is the more visible promise. */
const preferred = (alignment, size) => {
  if (!alignment) return size;
  if (!size) return alignment;
  return size.distance < alignment.distance ? size : alignment;
};

/**
 * Turn the chosen snap into an offset and a size change. Dragging a left or top
 * handle moves the origin as well, and the two kinds move it in opposite
 * directions: aligning that edge shrinks the object as it slides right, whereas
 * matching a size grows it leftward from a pinned opposite edge.
 */
const resolveResize = (edge, pick) => {
  if (!pick) return { offset: 0, size: 0 };
  const leading = edge === 'left' || edge === 'top';
  if (pick.kind === 'align') {
    return leading ? { offset: pick.delta, size: -pick.delta } : { offset: 0, size: pick.delta };
  }
  return leading ? { offset: -pick.delta, size: pick.delta } : { offset: 0, size: pick.delta };
};

/** Extent of a rect along the direction a guide runs. */
const spanOf = (rect, axis) =>
  axis === 'x'
    ? { start: rect.top, end: rect.top + rect.height }
    : { start: rect.left, end: rect.left + rect.width };

/**
 * Once an axis has settled, every candidate sitting exactly under a snapped edge
 * becomes a line. Candidates at the same position share one line, so three
 * objects on a common edge draw once rather than three times over.
 */
const guidesOn = (axis, snapped, candidates, slide, edges) => {
  const at = axis === 'x' ? xAt : yAt;
  const groups = new Map();

  edges.forEach((edge) => {
    const position = at(snapped, edge);
    candidates.forEach((candidate) => {
      if (Math.abs(candidate.position - position) > EPSILON) return;
      const group = groups.get(position) || { onSlide: false, rects: [snapped] };
      group.onSlide = group.onSlide || candidate.source === 'slide';
      if (candidate.source !== 'slide') group.rects.push(candidate.rect);
      groups.set(position, group);
    });
  });

  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([position, group]) => {
      if (group.onSlide) {
        return {
          type: 'align',
          axis,
          position,
          start: 0,
          end: axis === 'x' ? slide.height : slide.width,
        };
      }
      const spans = group.rects.map((rect) => spanOf(rect, axis));
      return {
        type: 'align',
        axis,
        position,
        start: Math.min(...spans.map((s) => s.start)) - GUIDE_OVERHANG_PX,
        end: Math.max(...spans.map((s) => s.end)) + GUIDE_OVERHANG_PX,
      };
    });
};

const markFor = (rect, dimension) =>
  dimension === 'width'
    ? {
        type: 'size',
        dimension,
        start: rect.left,
        end: rect.left + rect.width,
        at: rect.top + rect.height + SIZE_MARK_OFFSET_PX,
      }
    : {
        type: 'size',
        dimension,
        start: rect.top,
        end: rect.top + rect.height,
        at: rect.left + rect.width + SIZE_MARK_OFFSET_PX,
      };

/**
 * Marks fall out of the final geometry rather than out of the snap, so a resize
 * that snapped to a match and a move that happened to already match are the same
 * case. Only a dimension the gesture can change is considered.
 */
const sizeMarks = (final, targets, dimension, enabled) => {
  if (!enabled) return [];
  const size = dimension === 'width' ? final.width : final.height;
  const matched = targets.filter(
    (target) => Math.abs((dimension === 'width' ? target.width : target.height) - size) <= EPSILON
  );
  if (!matched.length) return [];
  return [final, ...matched].map((rect) => markFor(rect, dimension));
};

const NOTHING = { dx: 0, dy: 0, dw: 0, dh: 0, guides: [] };

/**
 * @param {{left,top,width,height}} active candidate rect in px, relative to the slide root
 * @param {Array<{id,left,top,width,height}>} targets the other objects on the slide
 * @param {{width,height}} slide measured size of the slide root
 * @param {{handle: string|null, altKey: boolean}} options null handle means a move
 */
export function computeSnap(active, targets, slide, options = {}) {
  const { handle = null, altKey = false } = options;
  if (altKey) return NOTHING;

  const xEdges = edgesFor(handle, 'x');
  const yEdges = edgesFor(handle, 'y');
  const xCandidates = candidatesOn('x', targets, slide);
  const yCandidates = candidatesOn('y', targets, slide);

  const xAlign = chooseAlignment(active, xCandidates, 'x', xEdges);
  const yAlign = chooseAlignment(active, yCandidates, 'y', yEdges);

  let dx = 0;
  let dy = 0;
  let dw = 0;
  let dh = 0;

  if (!handle) {
    dx = xAlign ? xAlign.delta : 0;
    dy = yAlign ? yAlign.delta : 0;
  } else {
    if (xEdges.length) {
      const pick = preferred(xAlign, chooseSize(active.width, targets.map((t) => t.width)));
      const { offset, size } = resolveResize(xEdges[0], pick);
      if (active.width + size >= MIN_SIZE_PX) {
        dx = offset;
        dw = size;
      }
    }
    if (yEdges.length) {
      const pick = preferred(yAlign, chooseSize(active.height, targets.map((t) => t.height)));
      const { offset, size } = resolveResize(yEdges[0], pick);
      if (active.height + size >= MIN_SIZE_PX) {
        dy = offset;
        dh = size;
      }
    }
  }

  const final = {
    left: active.left + dx,
    top: active.top + dy,
    width: active.width + dw,
    height: active.height + dh,
  };

  return {
    dx,
    dy,
    dw,
    dh,
    guides: [
      ...guidesOn('x', final, xCandidates, slide, xEdges),
      ...guidesOn('y', final, yCandidates, slide, yEdges),
      ...sizeMarks(final, targets, 'width', !handle || xEdges.length > 0),
      ...sizeMarks(final, targets, 'height', !handle || yEdges.length > 0),
    ],
  };
}
